if (!customElements.get('shop-the-look')) {
  class ShopTheLook extends HTMLElement {
    constructor() {
      super();
      this.items = [];
      this.infoCards = [];
      this.dots = [];
      this.total = 0;

      this.queue = [];
      this.ptr = 0;
      this.rotationCount = 0;
      this.isAnimating = false;

      this._section = null;
      this._scrollHandler = null;
      this._mq = null;
      this._desktopActive = false;
    }

    connectedCallback() {
      this.items = [...this.querySelectorAll('[data-stl-index]')];
      this.infoCards = [...this.querySelectorAll('[data-stl-info]')];
      this.dots = [...this.querySelectorAll('[data-stl-dot]')];
      this.total = this.items.length;
      this._section = this.closest('.section-shop-the-look');

      if (this.total === 0) return;

      this._mq = window.matchMedia('(min-width: 990px)');
      this._mq.addEventListener('change', (e) => {
        if (e.matches) {
          this._teardownMobile();
          this._setupDesktop();
        } else {
          this._teardownDesktop();
          this._setupMobile();
        }
      });

      if (this._mq.matches) {
        this._setupDesktop();
      } else {
        this._setupMobile();
      }
    }

    disconnectedCallback() {
      this._teardownDesktop();
      this._teardownMobile();
    }

    // ─── Shared queue initialisation ────────────────────────────────────────

    _initQueue() {
      // Build a sequential queue so forward rotation cycles products in order:
      // 0 → 1 → 2 → 3 → ... → N-1 → 0 → ...
      //
      // Queue layout: [N-1, 0, 1, 2, ..., N-2]
      //   queue[ptr=1] = items[0] = initial center
      //   queue[ptr+1] = items[1] = initial bottom-right (next)
      //   queue[ptr-1] = items[N-1] = initial top-left (wrap-around prev)
      this.queue = new Array(this.total);
      if (this.total > 0) {
        this.queue[0] = this.total - 1;
        for (let i = 1; i < this.total; i++) {
          this.queue[i] = i - 1;
        }
      }
      this.ptr = Math.min(1, this.total - 1);

      this._resetPositions();
    }

    // Snap all items back to initial positions without animation.
    // Called on desktop teardown so _initQueue gets a clean slate for mobile.
    _resetPositions() {
      const last = this.total - 1;
      this.items.forEach((item, i) => {
        item.classList.add('stl-item--no-transition');
        if (i === 0) {
          item.setAttribute('data-stl-position', 'center');
        } else if (i === 1 && this.total > 1) {
          item.setAttribute('data-stl-position', 'bottom-right');
        } else if (i === last && this.total > 2) {
          item.setAttribute('data-stl-position', 'top-left');
        } else {
          item.setAttribute('data-stl-position', 'hidden');
        }
      });
      this.items[0]?.offsetHeight; // force reflow
      this.items.forEach((item) => item.classList.remove('stl-item--no-transition'));
      this._updateInfo(0);
      this._updateDots(0);
      this.rotationCount = 0;
      this.isAnimating = false;
    }

    // ─── Desktop: Scroll Hijack ─────────────────────────────────────────────

    _setupDesktop() {
      if (this._desktopActive) return;
      this._desktopActive = true;

      this._initQueue();

      if (this._section) {
        this._section.style.setProperty('--stl-runway-count', this.total);
      }

      this._scrollHandler = () => this._onScroll();
      window.addEventListener('scroll', this._scrollHandler, { passive: true });
      this.addEventListener('click', this._handleDesktopClick);
      this._updateAccessibility();
    }

    _teardownDesktop() {
      if (!this._desktopActive) return;
      this._desktopActive = false;
      this.isAnimating = false;

      if (this._section) {
        this._section.style.removeProperty('--stl-runway-count');
      }
      if (this._scrollHandler) {
        window.removeEventListener('scroll', this._scrollHandler);
        this._scrollHandler = null;
      }
      this.removeEventListener('click', this._handleDesktopClick);
      this._resetPositions();
    }

    _handleDesktopClick = (e) => {
      const item = e.target.closest('[data-stl-index]');
      if (!item) return;
      if (item.getAttribute('data-stl-position') === 'center') {
        const url = item.getAttribute('data-stl-url');
        if (url) window.location.href = url;
      }
    };

    _onScroll() {
      if (!this._section || this.isAnimating) return;

      const sectionTop = this._section.getBoundingClientRect().top + window.scrollY;
      const runway = this._section.offsetHeight - window.innerHeight;
      if (runway <= 0) return;

      const scrolled = window.scrollY - sectionTop;
      const progress = Math.max(0, Math.min(1, scrolled / runway));
      const targetRotation = Math.round(progress * (this.total - 1));

      if (targetRotation > this.rotationCount) {
        this.rotationCount++;
        this._rotateForward();
      } else if (targetRotation < this.rotationCount) {
        this.rotationCount--;
        this._rotateBackward();
      }
    }

    // ─── Mobile: Tap-to-rotate carousel ────────────────────────────────────

    _setupMobile() {
      if (this.total < 2) return;

      this._initQueue();

      this._clickMobile = (e) => {
        const item = e.target.closest('[data-stl-index]');
        if (item) this._onMobileItemClick(item);
      };
      this._keydownMobile = (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          const item = e.target.closest('[data-stl-index]');
          if (item) {
            e.preventDefault();
            this._onMobileItemClick(item);
          }
        }
      };

      this.addEventListener('click', this._clickMobile);
      this.addEventListener('keydown', this._keydownMobile);
      this._updateAccessibility();
    }

    _teardownMobile() {
      if (this._clickMobile) {
        this.removeEventListener('click', this._clickMobile);
        this._clickMobile = null;
      }
      if (this._keydownMobile) {
        this.removeEventListener('keydown', this._keydownMobile);
        this._keydownMobile = null;
      }
    }

    _wrap(i) {
      return ((i % this.total) + this.total) % this.total;
    }

    _queueAt(offset) {
      return this.queue[this._wrap(this.ptr + offset)];
    }

    _onMobileItemClick(clickedItem) {
      const pos = clickedItem.getAttribute('data-stl-position');
      if (this.isAnimating) return;
      if (pos === 'center') {
        const url = clickedItem.getAttribute('data-stl-url');
        if (url) window.location.href = url;
        return;
      }
      if (pos === 'bottom-right') this._rotateForward();
      else if (pos === 'top-left') this._rotateBackward();
    }

    // ─── Shared rotation logic ───────────────────────────────────────────────

    _rotateForward() {
      this.isAnimating = true;

      const exitIdx = this._queueAt(-1);
      const toTLIdx = this._queueAt(0);
      const toCIdx = this._queueAt(1);
      const enterIdx = this._queueAt(2);

      this.ptr = this._wrap(this.ptr + 1);

      const exitItem = this.items[exitIdx];
      const toTL = this.items[toTLIdx];
      const toC = this.items[toCIdx];
      const enterItem = this.items[enterIdx];

      if (exitIdx === enterIdx) {
        const clone = exitItem.cloneNode(true);
        clone.removeAttribute('data-stl-index');
        exitItem.parentNode.appendChild(clone);
        clone.offsetHeight;
        clone.setAttribute('data-stl-position', 'offscreen-top-left');
        setTimeout(() => clone.remove(), 900);
      } else {
        exitItem.setAttribute('data-stl-position', 'offscreen-top-left');
      }

      toTL.setAttribute('data-stl-position', 'top-left');
      toC.setAttribute('data-stl-position', 'center');

      enterItem.classList.add('stl-item--no-transition');
      enterItem.setAttribute('data-stl-position', 'offscreen-bottom-right');
      enterItem.offsetHeight;
      enterItem.classList.remove('stl-item--no-transition');
      enterItem.setAttribute('data-stl-position', 'bottom-right');

      this._updateInfo(toCIdx);
      this._updateDots(toCIdx);
      this._updateAccessibility();

      setTimeout(() => {
        this.items.forEach((item) => {
          const p = item.getAttribute('data-stl-position');
          if (p && p.startsWith('offscreen')) {
            item.setAttribute('data-stl-position', 'hidden');
          }
        });
        this.isAnimating = false;
      }, 900);
    }

    _rotateBackward() {
      this.isAnimating = true;

      const exitIdx = this._queueAt(1);
      const toBRIdx = this._queueAt(0);
      const toCIdx = this._queueAt(-1);
      const enterIdx = this._queueAt(-2);

      this.ptr = this._wrap(this.ptr - 1);

      const exitItem = this.items[exitIdx];
      const toBR = this.items[toBRIdx];
      const toC = this.items[toCIdx];
      const enterItem = this.items[enterIdx];

      if (exitIdx === enterIdx) {
        const clone = exitItem.cloneNode(true);
        clone.removeAttribute('data-stl-index');
        exitItem.parentNode.appendChild(clone);
        clone.offsetHeight;
        clone.setAttribute('data-stl-position', 'offscreen-bottom-right');
        setTimeout(() => clone.remove(), 900);
      } else {
        exitItem.setAttribute('data-stl-position', 'offscreen-bottom-right');
      }

      toBR.setAttribute('data-stl-position', 'bottom-right');
      toC.setAttribute('data-stl-position', 'center');

      enterItem.classList.add('stl-item--no-transition');
      enterItem.setAttribute('data-stl-position', 'offscreen-top-left');
      enterItem.offsetHeight;
      enterItem.classList.remove('stl-item--no-transition');
      enterItem.setAttribute('data-stl-position', 'top-left');

      this._updateInfo(toCIdx);
      this._updateDots(toCIdx);
      this._updateAccessibility();

      setTimeout(() => {
        this.items.forEach((item) => {
          const p = item.getAttribute('data-stl-position');
          if (p && p.startsWith('offscreen')) {
            item.setAttribute('data-stl-position', 'hidden');
          }
        });
        this.isAnimating = false;
      }, 900);
    }

    // ─── Shared helpers ─────────────────────────────────────────────────────

    _updateAccessibility() {
      this.items.forEach((item) => {
        const pos = item.getAttribute('data-stl-position');
        const isCorner = pos === 'top-left' || pos === 'bottom-right';
        const isCenter = pos === 'center';
        const url = item.getAttribute('data-stl-url');
        const img = item.querySelector('img');
        const label = img ? img.alt : '';

        if (isCenter && url) {
          item.setAttribute('role', 'link');
          item.setAttribute('tabindex', '0');
          item.setAttribute('aria-label', label);
        } else if (isCorner && !this._desktopActive) {
          // Corner items are tappable only on mobile
          item.setAttribute('role', 'button');
          item.setAttribute('tabindex', '0');
          item.setAttribute('aria-label', label);
        } else {
          item.removeAttribute('role');
          item.setAttribute('tabindex', '-1');
          item.removeAttribute('aria-label');
        }
      });
    }

    _updateInfo(activeProductIndex) {
      const idx = String(activeProductIndex);
      this.infoCards.forEach((card) => {
        card.classList.toggle('is-active', card.getAttribute('data-stl-info') === idx);
      });
    }

    _updateDots(activeProductIndex) {
      const idx = String(activeProductIndex);
      this.dots.forEach((dot) => {
        dot.classList.toggle('is-active', dot.getAttribute('data-stl-dot') === idx);
      });
    }
  }

  customElements.define('shop-the-look', ShopTheLook);
}
